/**
 * Auth seam — the only module that talks to Clerk.
 *
 * VITE_AUTH_DISABLED=true skips Clerk entirely for local development; the API must
 * be started with AUTH_DISABLED=true as well. vite.config.ts refuses to produce a
 * production build with the flag set, so it can never ship to the portal.
 */

import { ClerkProvider, useAuth as useClerkAuth } from "@clerk/clerk-react";
import type { ReactNode } from "react";

export const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

export interface AppAuth {
  isLoaded: boolean;
  isSignedIn: boolean;
  getToken: () => Promise<string | null>;
}

const LOCAL_AUTH: AppAuth = {
  isLoaded: true,
  isSignedIn: true,
  getToken: () => Promise.resolve(null),
};

function useLocalAuth(): AppAuth {
  return LOCAL_AUTH;
}

function useClerkAppAuth(): AppAuth {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  // getToken is passed through unwrapped so its identity stays stable for useCallback deps.
  return { isLoaded, isSignedIn: isSignedIn === true, getToken };
}

/** Chosen once at module load, so hook order is constant for the life of the page. */
export const useAppAuth: () => AppAuth = AUTH_DISABLED ? useLocalAuth : useClerkAppAuth;

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_DISABLED) return <>{children}</>;
  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  return <ClerkProvider publishableKey={publishableKey}>{children}</ClerkProvider>;
}
